import { IfAbility, type SessionService } from '@efdigital/ll-addon-auth';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import LtiCopyUrlField from '@efdigital/ll-admin-v2/components/lti/copy-url-field.gts';
import LtiCreateToolModal from '@efdigital/ll-admin-v2/components/lti/create-tool-modal.gts';
import { EditLtiConsumedTool } from '@efdigital/ll-admin-v2/components/lti/edit-lti-consumed-tool.gts';
import { buildDeleteConsumedTool } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tools';
import type { LtiConsumedTool } from '@efdigital/ll-admin-v2/data/types/lti';
import type { LibraryClientsEditLtiIntegrationsPlatformModel } from '@efdigital/ll-admin-v2/routes/library/clients/client/edit/lti-integrations/platform';
import { alertLtiDeleteError } from '@efdigital/ll-admin-v2/utils/lti/delete-errors';
import {
  buildLtiIntegrationUrls,
  formatLtiIntegrationUrlsForCopy,
} from '@efdigital/ll-admin-v2/utils/lti/integration-urls';
import {
  findXceedTool,
  XCEED_PRODUCT,
} from '@efdigital/ll-admin-v2/utils/lti/products/xceed';
import {
  platformPhaseStatus,
  toolPhaseStatus,
} from '@efdigital/ll-admin-v2/utils/lti/setup-status';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface Signature {
  Args: {
    model: LibraryClientsEditLtiIntegrationsPlatformModel;
  };
}

export default class LtiIntegrationsPlatformToolTemplate extends Component<Signature> {
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked copiedAll = false;
  @tracked private _deletingTool = false;

  isDeleteDisabled = (isEditing: boolean): boolean =>
    isEditing || this._deletingTool;

  get tool() {
    return findXceedTool(this.args.model.tools);
  }

  get platformPhaseComplete(): boolean {
    return platformPhaseStatus(this.args.model.platform) === 'complete';
  }

  get status() {
    return toolPhaseStatus(this.tool);
  }

  get integrationUrls() {
    if (!this.tool) {
      return null;
    }

    return buildLtiIntegrationUrls(
      this.session.apis.systemsApi.host,
      this.args.model.platform.id,
      this.tool.id
    );
  }

  refreshRoute = () => {
    void this.router.refresh(
      'library.clients.client.edit.lti-integrations.platform'
    );
  };

  handleToolCreated = (_tool: LtiConsumedTool) => {
    this.refreshRoute();
  };

  deleteTool = async () => {
    const tool = this.tool;
    if (!tool) {
      return;
    }

    if (
      !confirm(
        `Are you sure you want to delete the ${XCEED_PRODUCT.name} tool registration? Delete all deployments for this tool first.`
      )
    ) {
      return;
    }

    this._deletingTool = true;
    try {
      await this.store.request(
        buildDeleteConsumedTool(tool, this.args.model.platform.id)
      );
      this.refreshRoute();
    } catch (error) {
      alertLtiDeleteError(error);
    } finally {
      this._deletingTool = false;
    }
  };

  copyAllUrls = async () => {
    if (!this.integrationUrls) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        formatLtiIntegrationUrlsForCopy(this.integrationUrls)
      );
      this.copiedAll = true;
      window.setTimeout(() => {
        this.copiedAll = false;
      }, 2000);
    } catch {
      // Clipboard may be unavailable in some contexts.
    }
  };

  <template>
    <div data-test-lti-phase-tool>
      {{#if this.tool}}
        <div class="card shadow-sm mb-4">
          <div class="card-body">
            <h4 class="h6 gud-eyebrow">Send to customer</h4>
            <p class="text-muted lh-sm">
              Share these URLs with the customer's LMS administrator so they can
              register
              {{XCEED_PRODUCT.name}}
              as a tool within their platform.
            </p>

            {{#if this.integrationUrls}}
              <div class="vstack gap-3 mb-3">
                <LtiCopyUrlField
                  @label="JWKS URL"
                  @value={{this.integrationUrls.jwks}}
                  @inputId="lti-url-jwks"
                />
                <LtiCopyUrlField
                  @label="Login initiate URL"
                  @value={{this.integrationUrls.initiate}}
                  @inputId="lti-url-initiate"
                />
                <LtiCopyUrlField
                  @label="Launch URL"
                  @value={{this.integrationUrls.launch}}
                  @inputId="lti-url-launch"
                />
              </div>
              <div class="d-flex justify-content-center">
                <button
                  type="button"
                  class="btn btn-outline-primary btn-md"
                  {{on "click" this.copyAllUrls}}
                >
                  {{#if this.copiedAll}}
                    <iconify-icon inline icon="ph:check" />
                    Copied all URLs
                  {{else}}
                    <iconify-icon inline icon="ph:copy" />
                    Copy all URLs
                  {{/if}}
                </button>
              </div>
            {{/if}}
          </div>
        </div>

        <EditContext as |context|>
          <div class="card shadow-sm mb-4">
            <div class="card-body">
              <IfAbility @ability="lti-integrations" @atLeast="editor">
                <:if>
                  <EditLtiConsumedTool
                    @item={{this.tool}}
                    @platformId={{@model.platform.id}}
                    @context={{context}}
                    @initialState="readonly"
                  >
                    <:help>

                      <p class="lh-sm">
                        Enter the values the customer's LMS administrator
                        provides after registering
                        {{XCEED_PRODUCT.name}}.
                      </p><p class="lh-sm">You should use
                        <code>TODO</code>
                        for any values not yet available.
                      </p>
                    </:help>
                  </EditLtiConsumedTool>
                </:if>
                <:else>
                  <dl class="row mb-0">
                    <dt class="col-sm-4">Platform client ID</dt>
                    <dd
                      class="col-sm-8 font-monospace"
                    >{{this.tool.platformData.client_id}}</dd>
                    <dt class="col-sm-4">JWKS URL</dt>
                    <dd
                      class="col-sm-8 font-monospace"
                    >{{this.tool.platformData.jwks_url}}</dd>
                    <dt class="col-sm-4">Authorization endpoint</dt>
                    <dd
                      class="col-sm-8 font-monospace"
                    >{{this.tool.platformData.authorizationEndpoint}}</dd>
                  </dl>
                </:else>
              </IfAbility>
            </div>
          </div>

          <IfAbility @ability="lti-integrations" @atLeast="editor">
            <:if>
              <div class="card border-danger shadow-sm mb-4">
                <div class="card-body">
                  <h3 class="h6 text-danger mb-2">Delete tool registration</h3>
                  <p class="text-muted small mb-3">
                    Remove the
                    {{XCEED_PRODUCT.name}}
                    tool from this platform. Delete all deployments for this
                    tool first.
                  </p>
                  <button
                    type="button"
                    class="btn btn-outline-danger"
                    disabled={{this.isDeleteDisabled context.isEditing}}
                    data-test-lti-delete-tool
                    {{on "click" this.deleteTool}}
                  >
                    {{#if this._deletingTool}}
                      Deleting…
                    {{else}}
                      <iconify-icon inline icon="ph:trash" />
                      Delete tool
                    {{/if}}
                  </button>
                </div>
              </div>
            </:if>
          </IfAbility>
        </EditContext>
      {{else}}
        <p class="text-muted mb-3">
          Register
          {{XCEED_PRODUCT.name}}
          for this platform to generate the URLs to send to the customer.
        </p>
        <EditContext as |context|>
          <IfAbility @ability="lti-integrations" @atLeast="editor">
            <:if>
              <LtiCreateToolModal
                @platformId={{@model.platform.id}}
                @onSaved={{this.handleToolCreated}}
              >
                <:trigger as |openModal|>
                  <button
                    type="button"
                    class="btn btn-primary"
                    {{on "click" openModal}}
                    disabled={{context.isEditing}}
                    data-test-lti-register-xceed
                  >{{XCEED_PRODUCT.registerButtonLabel}}</button>
                </:trigger>
              </LtiCreateToolModal>
            </:if>
            <:else>
              <div class="alert alert-warning">
                You do not have permission to administer LTI integrations.
              </div>
            </:else>
          </IfAbility>
        </EditContext>
      {{/if}}
    </div>
  </template>
}
